import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ReactComponent as ChevronIcon } from 'assets/icons/chevron.svg';
import Checkbox from 'components/Checkbox/Checkbox';
import Tooltip from 'components/Tooltip/Tooltip';
import { SELECTED_ITEMS } from 'constants/formFields';
import { TOOLTIP_POSITIONS } from 'constants/interfaces';
import { KEYS_FIELDS_QUESTION } from 'constants/materialAssessment';
import getValueChainNames from 'utils/valueChainHelpers';

import type IQuestionsItem from 'store/slices/materialAssessment/interfaces/IQuestionsItem';
import type IStakeholderGroupsQuestionsItem from 'store/slices/materialAssessment/interfaces/IStakeholderGroupsQuestionsItem';
import type ITable from './interfaces/ITable';

import styles from './scss/Table.module.scss';

function TableQuestion({
  headCells, control, isLineMark, items, headCellsMain, hasMore, loadMore,
  expandedRowId, setExpandedRowId, questions, onSelectItem, vcOptions,
  isViewOnly = false,
}: Readonly<ITable>) {
  const { t } = useTranslation();

  const toggleRow = (id: number) => {
    setExpandedRowId(expandedRowId === id ? -1 : id);
  };

  const renderMainTableCell = (item: IStakeholderGroupsQuestionsItem, cellId: string) => {
    switch (cellId) {
      case KEYS_FIELDS_QUESTION.QUESTION:
        return (
          <span className={styles.questionType}>
            {item.type || '-'}
          </span>
        );
      case KEYS_FIELDS_QUESTION.SOURCE:
        return (
          <span className={styles.questionSourceType}>
            {item.sourceType || '-'}
          </span>
        );
      case KEYS_FIELDS_QUESTION.COMPANY_NAME:
        return (
          <span className={styles.companyName}>
            {item.companyName || '-'}
          </span>
        );
      case KEYS_FIELDS_QUESTION.EVIDENCE_SOURCE:
        return (
          <span className={styles.source}>
            {item.source || '-'}
          </span>
        );
      case KEYS_FIELDS_QUESTION.QUESTION_GROUP:
        return (
          <Tooltip hint={item.title} hintClassName={styles.tooltip}>
            <span className={styles.source}>
              {item.title || '-'}
            </span>
          </Tooltip>
        );
      case KEYS_FIELDS_QUESTION.QUESTION_NAME:
        return (
          <Tooltip hint={item.title} hintClassName={styles.tooltip}>
            <span className={styles.checkboxLabel}>
              {item.title || '-'}
            </span>
          </Tooltip>
        );
      case KEYS_FIELDS_QUESTION.VALUE_CHAIN:
        return (
          <span className={styles.source}>
            {getValueChainNames(item.valueChainMapIds, vcOptions)}
          </span>
        );
      default:
        return '';
    }
  };

  const renderMiniTableCell = (item: IQuestionsItem, cellId: string, index: number) => {
    switch (cellId) {
      case KEYS_FIELDS_QUESTION.QUESTION:
        return (
          <span className={styles.questionType}>
            {item.type || '-'}
          </span>
        );
      case KEYS_FIELDS_QUESTION.SOURCE:
        return (
          <span className={styles.questionSourceType}>
            {item.sourceType || '-'}
          </span>
        );
      case KEYS_FIELDS_QUESTION.COMPANY_NAME:
        return (
          <span className={styles.companyName}>
            {item.companyName || '-'}
          </span>
        );
      case KEYS_FIELDS_QUESTION.EVIDENCE_SOURCE:
        return (
          <span className={styles.source}>
            {item.source || '-'}
          </span>
        );
      case KEYS_FIELDS_QUESTION.QUESTION_GROUP:
        return (
          <Tooltip hint={item.text} hintClassName={styles.tooltipMini}>
            <span className={styles.source}>
              {item.text || '-'}
            </span>
          </Tooltip>
        );
      case KEYS_FIELDS_QUESTION.QUESTION_NAME:
        return (
          <Tooltip
            hint={item.text}
            hintClassName={styles.tooltipMini}
            position={index === 0 ? TOOLTIP_POSITIONS.BOTTOM : undefined}
          >
            <span className={styles.titleMini}>
              {item.text || '-'}
            </span>
          </Tooltip>
        );
      case KEYS_FIELDS_QUESTION.VALUE_CHAIN:
        return (
          <span className={styles.source}>
            {getValueChainNames([item.valueChainMapId], vcOptions)}
          </span>
        );
      default:
        return '';
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.tableScrollWrapper}>
        <div className={styles.tableHeader}>
          <div className={styles.checkboxColumn} />
          <div className={styles.expandColumn} />
          {headCellsMain.map(({
            id, label,
          }) => (
            <div key={[id, label].join('_')} className={styles.headerCell}>
              <span>{t(label)}</span>
            </div>
          ))}
        </div>
        <InfiniteScroll
          dataLength={items?.length || 0}
          next={loadMore}
          hasMore={hasMore || false}
          loader={false}
        >
          <div className={styles.tableBodyScroll}>
            <div className={styles.tableBody}>
              {items && items?.length > 0 ? items?.map((item, index) => {
                const itemId = item.id;
                const isExpanded = expandedRowId === itemId;
                return (
                  <div key={[item.id, index].join('_')} className={styles.tableRowGroup}>
                    <div className={styles.tableRow}>
                      <div className={styles.checkboxColumn}>
                        <Controller
                          control={control}
                          name={SELECTED_ITEMS}
                          render={({ field: { onChange, value, disabled } }) => (
                            <Checkbox
                              key={item.id}
                              name={`${SELECTED_ITEMS}-${item.id}`}
                              checked={!!value.includes(item.id)}
                              value={item.id}
                              disabled={disabled || isViewOnly}
                              onChange={(event) => {
                                onChange(event.target.checked
                                  ? [...value, item.id]
                                  : value.filter((selectedItem) => selectedItem !== item.id));
                                onSelectItem(item.id, event.target.checked);
                              }}
                              label={false}
                              isLineMark={isLineMark}
                            />
                          )}
                        />
                      </div>
                      <div className={styles.expandColumn}>
                        <button
                          type="button"
                          className={`${styles.expandButton} ${isExpanded ? styles.expanded : ''}`}
                          onClick={() => toggleRow(itemId)}
                          aria-label={isExpanded ? t('common.collapse') : t('common.expand')}
                        >
                          <ChevronIcon />
                        </button>
                      </div>
                      <div className={styles.tableCell}>
                        <Tooltip hint={item.title} hintClassName={styles.tooltip}>
                          <span className={styles.checkboxLabel}>{item.title}</span>
                        </Tooltip>
                      </div>
                      {headCellsMain.filter((cell) => !cell.isFirstCell).map((cell) => (
                        <div key={cell.id} className={styles.tableCell}>
                          {renderMainTableCell(item, cell.id)}
                        </div>
                      ))}
                    </div>
                    {isExpanded && questions && questions.length > 0 ? (
                      <div className={styles.miniTableContainer}>
                        <div className={styles.miniTableHeader}>
                          {headCells.map((cell) => (
                            <div
                              key={cell.id}
                              className={styles.miniHeaderCell}
                            >
                              {t(cell.label)}
                            </div>
                          ))}
                        </div>
                        <div className={styles.miniTableBody}>
                          {questions.map((subItem, indexQue) => (
                            <div key={subItem.id} className={styles.miniTableRow}>
                              {headCells.map((cell) => (
                                <div key={cell.id} className={styles.miniTableCell}>
                                  {renderMiniTableCell(subItem, cell.id, indexQue)}
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              }) : null}
            </div>
          </div>
        </InfiniteScroll>
      </div>
    </div>

  );
}

export default TableQuestion;
